// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import IconCheckmark from "@conklin/XDL/Icons/IconCheckmark";
import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import { xBaseTheme } from "@conklin/XDL/types";
import { color } from "@conklin/XDL/Utilities";
import CheckboxPresentationPropsDefault from "@insite/mobius/Checkbox/presentationProps";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import { css } from "styled-components";

interface CheckboxSizesProps {
    fontSize: number;
    iconSize: number;
    borderRadius: number;
}

export const checkboxSizes: {
    [key: string]: CheckboxSizesProps;
} = {
    default: {
        fontSize: 12,
        iconSize: 13,
        borderRadius: 1,
    },
    small: {
        fontSize: 10,
        iconSize: 13,
        borderRadius: 1,
    },
};

export default {
    defaultProps: {
        ...CheckboxPresentationPropsDefault,
        mergeCss: true,
        typographyProps: {
            mergeCss: true,
            size: 12,
            weight: fontConfig.weight.regular,
            css: css`
                letter-spacing: 0;
                line-height: 15px;
            `,
        },
        iconProps: {
            src: IconCheckmark,
            size: 9,
        },
        indeterminateIconProps: {
            src: "Minus",
            size: 12,
        },
        css: css`
            cursor: ${({ disabled }: any) => (disabled ? "not-allowed" : "pointer")};

            span[role="checkbox"] {
                border-radius: ${({ _sizeVariant }: any) => checkboxSizes[_sizeVariant].borderRadius}px;
                min-width: ${({ _sizeVariant }: any) => checkboxSizes[_sizeVariant].iconSize}px;
                height: ${({ _sizeVariant }: any) => checkboxSizes[_sizeVariant].iconSize}px;
                border: 1px solid ${color("common.ui4")};
                background-color: ${color("common.ui2")};
                transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
                cursor: pointer;
                padding: 1px;
                justify-content: center;
                
                &[aria-disabled="true"] {
                    cursor: not-allowed;
                }

                &[aria-checked="true"]:not([aria-disabled="true"]),
                &[aria-checked="mixed"]:not([aria-disabled="true"]) {
                    background: ${color("common.ui11")};
                    border-color: ${color("common.ui11")};
                    color: ${color("common.white")};
                }
                &:focus {
                    outline: none;
                    box-shadow: none;
                }
                + label{
                    margin-left: 8px;
                }
            }

            &:hover {
                span[role="checkbox"] {
                    &[aria-checked="true"]:not([aria-disabled="true"]),
                    &[aria-checked="mixed"]:not([aria-disabled="true"]) {
                        background: ${color("common.ui10")};
                        border-color: ${color("common.ui10")};
                    }
                   
                }
                div[role="checkbox"] {
                    fill: ${color("common.ui1")};
                    color: ${color("common.ui1")};
                }
            }

            &:focus {
                span[role="checkbox"] {
                    outline: none;
                    box-shadow: none;
                }
            }
        `,
    },
    groupDefaultProps: {
        mergeCss: true,
        css: css`
            & > ${TypographyStyle}:empty {
                display: none;
            }
        `,
    },
} as xBaseTheme["checkbox"];
